import { useEffect, useRef } from "react";
import gsap from "gsap";
import { buildLocalSegmentPath, waypointFor } from "@/lib/genieWaypoints";
import { genieTransitionBus } from "@/lib/genieTransition";

const PATH_STYLE = {
    stroke: "#FDB927",
    strokeWidth: 3,
    strokeLinecap: "round",
    fill: "none",
};

export const MagicPathTransition = () => {
    const pathRef = useRef(null);
    const glowRef = useRef(null);
    const tweenRef = useRef(null);
    const playResolveRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

        const kill = () => {
            tweenRef.current?.kill();
            tweenRef.current = null;
            if (pathRef.current) gsap.set(pathRef.current, { opacity: 0 });
            if (glowRef.current) gsap.set(glowRef.current, { opacity: 0 });
            if (playResolveRef.current) {
                playResolveRef.current();
                playResolveRef.current = null;
            }
        };

        const play = (_fromId, toId) =>
            new Promise((resolve) => {
                kill();
                playResolveRef.current = resolve;
                const wp = waypointFor(toId);
                if (!wp || !pathRef.current) {
                    playResolveRef.current = null;
                    resolve();
                    return;
                }

                const { d, metrics } = buildLocalSegmentPath(wp);
                pathRef.current.setAttribute("d", d);
                if (glowRef.current) glowRef.current.setAttribute("d", d);

                const length = pathRef.current.getTotalLength();
                gsap.set(pathRef.current, {
                    ...PATH_STYLE,
                    strokeWidth: metrics.strokeWidth,
                    strokeDasharray: length,
                    strokeDashoffset: length,
                    opacity: metrics.opacity,
                });
                gsap.set(glowRef.current, {
                    stroke: "#FDB927",
                    strokeWidth: metrics.glowWidth,
                    strokeLinecap: "round",
                    fill: "none",
                    strokeDasharray: length,
                    strokeDashoffset: length,
                    opacity: metrics.opacity * 0.22,
                });

                tweenRef.current = gsap.to([pathRef.current, glowRef.current], {
                    strokeDashoffset: 0,
                    duration: 0.65,
                    ease: "power2.out",
                    onComplete: () => {
                        gsap.to([pathRef.current, glowRef.current], {
                            opacity: 0,
                            duration: 0.35,
                            delay: 0.08,
                            onComplete: () => {
                                playResolveRef.current = null;
                                resolve();
                            },
                        });
                    },
                });
            });

        genieTransitionBus.registerPath({ kill, play });

        return () => {
            kill();
            genieTransitionBus.registerPath(null);
        };
    }, []);

    return (
        <div
            className="pointer-events-none fixed inset-0 z-[6]"
            aria-hidden="true"
            data-testid="magic-path-transition"
        >
            <svg className="h-full w-full" fill="none">
                <path ref={glowRef} />
                <path ref={pathRef} strokeDasharray="0.5 18" />
            </svg>
        </div>
    );
};
